// 전환 측정. GA4 와 Meta 픽셀 양쪽에 같은 지점에서 쏜다.
//
// 측정 ID 는 .env 로 주입한다. 비어 있으면 조용히 무시하므로
// 로컬·개발 환경에서 부작용이 없다.
//   GA4   : REACT_APP_GA_MEASUREMENT_ID
//   Meta  : REACT_APP_META_PIXEL_ID   (인스타·페이스북 광고용)
//
// GA4 는 "어디서 들어와 무엇을 했나" 를 보는 쪽, Meta 픽셀은 광고를 최적화하고
// 들어왔다 나간 사람에게 다시 노출하는 쪽이다. 역할이 달라 둘 다 필요하다.

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const NAVER_ID = process.env.REACT_APP_NAVER_ANALYTICS_ID;

let initialized = false;
let metaInitialized = false;
let naverInitialized = false;
// 첫 화면은 init 이 알린다. 라우터가 마운트되며 부르는 첫 한 번은 흘린다.
let routerFirstCallSkipped = false;

export const initAnalytics = () => {
    initGa();
    initMetaPixel();
    initNaverAnalytics();
};

const initGa = () => {
    if (initialized || !MEASUREMENT_ID || typeof window === "undefined") return;
    initialized = true;

    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.appendChild(s);

    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = gtag;
    gtag("js", new Date());
    gtag("config", MEASUREMENT_ID, { send_page_view: true });
};

/** Meta(페이스북·인스타그램) 픽셀. 광고를 돌리지 않으면 ID 를 비워 두면 된다. */
const initMetaPixel = () => {
    if (metaInitialized || !META_PIXEL_ID || typeof window === "undefined") return;
    metaInitialized = true;

    // 메타가 배포하는 표준 스니펫. 스크립트가 늦게 떠도 호출이 유실되지 않도록
    // fbq 를 먼저 만들어 큐에 쌓아 둔다.
    /* eslint-disable */
    !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = true;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    /* eslint-enable */

    window.fbq("init", META_PIXEL_ID);
    window.fbq("track", "PageView");
};

/**
 * 네이버 애널리틱스(wcslog).
 * 검색 유입의 상당수가 네이버라 GA4 와 별개로 붙인다. 네이버는 자사
 * 검색·블로그·카페에서 들어온 경로를 GA4 보다 자세히 보여준다.
 *
 * 네이버가 주는 스니펫은 스크립트가 이미 떠 있다고 보고 바로 wcs_do() 를
 * 부르지만, 여기서는 비동기로 싣기 때문에 onload 에서 부른다.
 */
const initNaverAnalytics = () => {
    if (naverInitialized || !NAVER_ID || typeof window === "undefined") return;
    naverInitialized = true;

    window.wcs_add = window.wcs_add || {};
    window.wcs_add["wa"] = NAVER_ID;

    const s = document.createElement("script");
    s.async = true;
    s.src = "https://wcs.pstatic.net/wcslog.js";
    s.onload = naverPageView;
    document.head.appendChild(s);
};

/** 네이버에 화면 하나를 알린다. 스크립트가 아직이면 조용히 건너뛴다. */
const naverPageView = () => {
    if (typeof window === "undefined" || !window.wcs || !window.wcs_do) return;
    try {
        // 유입 경로(검색어·출처) 집계. 화면이 바뀔 때마다 다시 불러야 한다.
        if (typeof window.wcs.inflow === "function") window.wcs.inflow();
        window.wcs_do();
    } catch (e) {
        // 집계 실패가 화면을 막아서는 안 된다
    }
};

/**
 * 우리 이벤트 이름 → Meta 표준 이벤트.
 * 표준 이벤트로 보내야 광고 최적화 목표로 고를 수 있다.
 * 매칭되는 표준 이벤트가 없으면 이름 그대로 맞춤 이벤트로 보낸다.
 */
const META_STANDARD = {
    generate_lead: "Lead",
    contact_click: "Contact",
    view_item_list: "ViewContent",
    select_item: "ViewContent",
};

const trackMeta = (event, params) => {
    if (typeof window === "undefined" || typeof window.fbq !== "function") return;
    const standard = META_STANDARD[event];
    if (standard) window.fbq("track", standard, params);
    else window.fbq("trackCustom", event, params);
};

/**
 * 화면 전환을 알린다.
 * SPA 는 주소가 바뀌어도 페이지를 새로 읽지 않아 픽셀이 모른다. 이게 빠지면
 * "견적 페이지까지 왔다가 나간 사람" 같은 리타게팅 모수가 쌓이지 않는다.
 * (GA4 는 '향상된 측정'이 기본으로 켜져 있어 따로 쏘지 않는다.)
 */
export const trackPageView = () => {
    if (typeof window === "undefined") return;
    // 첫 진입은 init 이 이미 알렸다. 라우터의 첫 호출을 흘려 중복을 막는다.
    if (!routerFirstCallSkipped) {
        routerFirstCallSkipped = true;
        return;
    }
    if (typeof window.fbq === "function") window.fbq("track", "PageView");
    naverPageView();
};

/** 이벤트 전송. GA4 권장 이벤트명을 그대로 써서 기본 리포트가 바로 동작하게 한다. */
export const track = (event, params = {}) => {
    try {
        if (typeof window !== "undefined" && typeof window.gtag === "function") {
            window.gtag("event", event, params);
        }
        trackMeta(event, params);
        if (process.env.NODE_ENV === "development") {
            console.debug("[analytics]", event, params);
        }
    } catch (e) {
        // 추적 실패가 화면 동작을 막아서는 안 된다
    }
};

// 문의 유입 측정에 필요한 최소 집합
export const trackFormStart = () => track("form_start", { form_id: "custom_order" });

export const trackLead = ({ hasFile = false, hasEmail = false, hasPhone = false } = {}) =>
    track("generate_lead", {
        form_id: "custom_order",
        has_file: hasFile,
        contact_method: hasEmail && hasPhone ? "both" : hasEmail ? "email" : "phone",
    });

export const trackContactClick = (method) => track("contact_click", { method });

export const trackItemListView = (count) =>
    track("view_item_list", { item_list_name: "product_index", item_count: count });

export const trackItemSelect = (itemName) =>
    track("select_item", { item_list_name: "product_index", item_name: itemName });
